import { GetServerSideProps, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
// Design system
import PageBanner from '@utmb/features/page-banner/PageBanner';
import { Layout } from 'src/layouts/layout-base';
// Features
import { SlicesHandler } from '@utmb/features/slices-handler/SlicesHandler';
// Interfaces
import { LangEnum, PageDefaultProps, PageView } from 'src/types';
// Context, utils
import { i18n } from 'next-i18next.config';
import { PageProviders } from 'src/contexts';
import isMobile from 'src/utils/is-mobile';
import { fetchAPI } from 'src/utils/utmb';

interface PageProps extends PageDefaultProps, PageView {}

const IndexPage: NextPage<PageProps> = ({
    banner,
    breadcrumbs,
    contentLocale,
    event,
    eventsTopBar,
    footer,
    host,
    isMobile,
    mobileBottomBar,
    navigation,
    noIndex,
    simpleMenu,
    seo,
    slices,
    partners,
    languages,
}) => {
    return (
        <PageProviders>
            <Layout
                {...seo}
                contentLocale={contentLocale}
                host={host}
                isMobile={isMobile}
                eventsTopBar={eventsTopBar}
                mobileBottomBar={mobileBottomBar}
                navigation={navigation}
                simpleMenu={simpleMenu}
                footer={footer}
                event={event}
                partners={partners}
                languages={languages}
                breadcrumbs={breadcrumbs}
                noIndex={noIndex}
            >
                {banner && <PageBanner {...banner} />}
                <SlicesHandler slices={slices} />
            </Layout>
        </PageProviders>
    );
};

export default IndexPage;

export const getServerSideProps: GetServerSideProps<PageView> = async ({ locale, defaultLocale, req, params }) => {
    try {
        // Translate
        const lang: LangEnum = (locale || defaultLocale || i18n.defaultLocale) as LangEnum;
        const translations = await serverSideTranslations(lang, ['common']);

        const host = req.headers.host;

        // Page Content
        const level1 = params!.level1 as string;
        const level2 = params!.level2 as string;
        const level3 = params!.level3 as string;
        const page = await fetchAPI(req, '/content/pages', `/${level1}/${level2}/${level3}`, { lang });
        // if error
        if (!page) return { notFound: true };
        const { banner, noIndex, seo, slices, slug, breadcrumbs, locale: contentLocale } = page;

        return {
            props: {
                banner,
                breadcrumbs,
                contentLocale,
                host,
                isMobile: isMobile(req),
                locale: lang,
                noIndex,
                seo,
                slices,
                slug,
                ...translations,
            },
        };
    } catch (error) {
        return {
            notFound: true,
        };
    }
};
